import { memo, useRef } from 'react';

import { FileTextOutlined } from '@ant-design/icons';
import { Button, Input } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { useIntl } from 'react-intl';

import Title from '@src/components/BoxContainer/components/Title';
import { KeyValueWidget } from '@src/components/KeyValueWidget';
import IntlMessages from '@src/util/IntlMessages';

import type { LocationAlertFormProps } from './LocationAlertForm.types';
import styles from './styles.module.less';

const LocationAlertForm = ({ onSubmit, onChange, loading }: LocationAlertFormProps) => {
  const intl = useIntl();
  const textAreaRef = useRef<TextAreaRef>(null);

  const keepFieldVisible = () => {
    window.setTimeout(() => {
      textAreaRef.current?.resizableTextArea?.textArea?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }, 250);
  };

  return (
    <div className={styles.locationAlertForm}>
      <div className={styles.scrollArea}>
        <div className={styles.formBody}>
          <div className={styles.iconWrap} aria-hidden>
            <FileTextOutlined />
          </div>
          <div className={styles.copyBlock}>
            <p className={styles.formTitle}>
              <IntlMessages id="location.alert.form.title" />
            </p>
            <p className={styles.formSubtitle}>
              <IntlMessages id="location.alert.form.subtitle" />
            </p>
          </div>

          <div className={styles.fieldBlock}>
            <KeyValueWidget
              label={
                <div className={styles.descriptionTitle}>
                  <Title.LabelForm
                    value={<IntlMessages id="location.alert.description.title" />}
                  />
                  <span className={styles.optionalLabel}>
                    <IntlMessages id="location.alert.description.title.optional" />
                  </span>
                </div>
              }
              value={
                <Input.TextArea
                  ref={textAreaRef}
                  onChange={e => onChange(e.target.value)}
                  onFocus={keepFieldVisible}
                  placeholder={intl.formatMessage({
                    id: 'location.alert.description.placeholder',
                  })}
                  size="large"
                  maxLength={500}
                  showCount
                  autoSize={{ minRows: 3, maxRows: 5 }}
                  enterKeyHint="done"
                />
              }
            />
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <Button
          block
          className={styles.sendButton}
          loading={loading}
          disabled={loading}
          onClick={onSubmit}
          type="primary"
          size="large"
        >
          <IntlMessages id="button.send" />
        </Button>
      </div>
    </div>
  );
};

export default memo(LocationAlertForm);
